import type { ContentBlock } from '@/hooks/useBlogDetail';

function ParagraphBlock({ html }: { html: string }) {
  return (
    <p
      className="text-[14px] leading-[1.7] text-secondary-600"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function Heading2Block({ text, id }: { text: string; id: string }) {
  return (
    <h2
      id={id}
      className="scroll-mt-24 text-[20px] font-extrabold leading-[1.25] tracking-[-0.02em] text-secondary-900 lg:text-[24px]"
    >
      {text}
    </h2>
  );
}

function Heading3Block({ text, id }: { text: string; id: string }) {
  return (
    <h3
      id={id}
      className="scroll-mt-24 text-[16px] font-bold leading-[1.3] tracking-[-0.01em] text-secondary-900 lg:text-[18px]"
    >
      {text}
    </h3>
  );
}

function ImageBlock({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="relative w-full overflow-hidden rounded-xl">
      <img src={src} alt={alt} className="h-auto w-full object-cover" />
    </div>
  );
}

function OrderedListBlock({ items }: { items: string[] }) {
  return (
    <ol className="flex list-decimal flex-col gap-3 pl-5 text-[14px] leading-[1.7] text-secondary-600">
      {items.map((item, i) => (
        <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
      ))}
    </ol>
  );
}

function UnorderedListBlock({ items }: { items: string[] }) {
  return (
    <ul className="flex list-disc flex-col gap-3 pl-5 text-[14px] leading-[1.7] text-secondary-600">
      {items.map((item, i) => (
        <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
      ))}
    </ul>
  );
}

function TableBlock({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          <tr className="border-b border-secondary-200">
            {headers.map((h, i) => (
              <th
                key={i}
                className="bg-secondary-50 px-3 py-2.5 text-left font-bold text-secondary-900 first:rounded-tl-lg last:rounded-tr-lg"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr
              key={ri}
              className="border-b border-secondary-100 last:border-b-0"
            >
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className="px-3 py-2.5 align-top text-secondary-600"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DividerBlock() {
  return <hr className="border-secondary-200" />;
}

export default function ArticleRenderer({ blocks }: { blocks: ContentBlock[] }) {
  return (
    <div className="article-content flex flex-col gap-4 [&_a]:text-primary-600 [&_a]:underline [&_a]:hover:text-primary-700 [&_strong]:font-semibold [&_strong]:text-secondary-900 [&_code]:rounded-md [&_code]:bg-secondary-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[12.5px] [&_code]:font-mono [&_code]:text-secondary-700">
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'paragraph':
            return <ParagraphBlock key={index} html={block.html} />;
          case 'heading2':
            return (
              <Heading2Block
                key={index}
                text={block.text}
                id={block.id}
              />
            );
          case 'heading3':
            return (
              <Heading3Block
                key={index}
                text={block.text}
                id={block.id}
              />
            );
          case 'image':
            return <ImageBlock key={index} src={block.src} alt={block.alt} />;
          case 'orderedList':
            return <OrderedListBlock key={index} items={block.items} />;
          case 'unorderedList':
            return <UnorderedListBlock key={index} items={block.items} />;
          case 'table':
            return (
              <TableBlock
                key={index}
                headers={block.headers}
                rows={block.rows}
              />
            );
          case 'divider':
            return <DividerBlock key={index} />;
          default:
            return null;
        }
      })}
    </div>
  );
}